import { useState, useCallback } from "react";

/**
 * Persists a background job's identity to localStorage so a screen can RE-ATTACH to a
 * still-running job after the user navigates away (which unmounts the component and
 * would otherwise drop the job id held only in local state / a closure).
 *
 * Stores an arbitrary small JSON record (e.g. `{ jobId, step, docId }`) under `key`
 * and rehydrates it on mount. This is the generic form of the existing `lvActiveUpload`
 * pattern in LVWorkflow. Returns `[record, save, clear]`; `save(null)` also clears.
 */
export default function useActiveJobId(key) {
  const [record, setRecord] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(key) || "null");
    } catch {
      return null;
    }
  });

  const save = useCallback((rec) => {
    setRecord(rec);
    try {
      if (rec) localStorage.setItem(key, JSON.stringify(rec));
      else localStorage.removeItem(key);
    } catch {
      /* storage unavailable (private mode / quota) — degrade to in-memory only */
    }
  }, [key]);

  const clear = useCallback(() => save(null), [save]);

  return [record, save, clear];
}
